import {useMemo, useState} from "react";
import {TransportAssocEntity} from "@ots-frontend/ots-data";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useDebouncedValue} from "@mantine/hooks";

export type UseTicketDataTableFilterProps = Omit<UseDataTableFilterProps<TransportAssocEntity>, 'filterConfig'>

export function useTicketDataTableFilter(props: UseTicketDataTableFilterProps): UseDataTableFilterResult<TransportAssocEntity> {
  const {records} = props;

  const [idQuery, setIdQuery] = useState<string | undefined>(undefined);
  const [idDebouncedQuery] = useDebouncedValue(idQuery, 200);

  const [pickupQuery, setPickupQuery] = useState<string | undefined>(undefined);
  const [pickupDebouncedQuery] = useDebouncedValue(pickupQuery, 200);

  const [deliveryQuery, setDeliveryQuery] = useState<string | undefined>(undefined);
  const [deliveryDebouncedQuery] = useDebouncedValue(deliveryQuery, 200);

  const [freightTypeQuery, setFreightTypeQuery] = useState<string | undefined>(undefined);
  const [freightTypeDebouncedQuery] = useDebouncedValue(freightTypeQuery, 200);

  const [serviceTypeQuery, setServiceTypeQuery] = useState<string | undefined>(undefined);
  const [serviceTypeDebouncedQuery] = useDebouncedValue(serviceTypeQuery, 200);

  const [truckCompanyQuery, setTruckCompanyQuery] = useState<string | undefined>(undefined);
  const [truckCompanyDebouncedQuery] = useDebouncedValue(truckCompanyQuery, 200);

  const filterConfig: DataTableFilterConfig<TransportAssocEntity> = useMemo(() => {
    return {
      "id": {
        setQuery: setIdQuery,
        query: idQuery,
        debouncedQuery: idDebouncedQuery,
        filter: (record, query) => !record.resourceIdentifiers.join(",").toLowerCase().includes(query.toLowerCase())
      },
      "pickup": {
        setQuery: setPickupQuery,
        query: pickupQuery,
        debouncedQuery: pickupDebouncedQuery,
        filter: (record, query) => !record.pickupTransport?.siteName || !record.pickupTransport.siteName.toLowerCase().includes(query.toLowerCase())
      },
      "delivery": {
        setQuery: setDeliveryQuery,
        query: deliveryQuery,
        debouncedQuery: deliveryDebouncedQuery,
        filter: (record, query) => !record.deliveryTransport?.siteName || !record.deliveryTransport.siteName.toLowerCase().includes(query.toLowerCase())
      },
      "freightType": {
        setQuery: setFreightTypeQuery,
        query: freightTypeQuery,
        debouncedQuery: freightTypeDebouncedQuery,
        filter: (record, query) => !record.pickupTransport?.waterTypeName || !record.pickupTransport.waterTypeName.toLowerCase().includes(query.toLowerCase())
      },
      "serviceType": {
        setQuery: setServiceTypeQuery,
        query: serviceTypeQuery,
        debouncedQuery: serviceTypeDebouncedQuery,
        filter: (record, query) => !record.pickupTransport?.serviceTypeName || !record.pickupTransport.serviceTypeName.toLowerCase().includes(query.toLowerCase())
      },
      "truckCompany": {
        setQuery: setTruckCompanyQuery,
        query: truckCompanyQuery,
        debouncedQuery: truckCompanyDebouncedQuery,
        filter: (record, query) => {
          const companyName = record.pickupTransport?.trailerParentCompanyName ?? record.pickupTransport?.trailerCompanyName
          return !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
        }
      },
    }
  }, [
    deliveryDebouncedQuery,
    deliveryQuery,
    freightTypeDebouncedQuery,
    freightTypeQuery,
    idDebouncedQuery,
    idQuery,
    pickupDebouncedQuery,
    pickupQuery,
    serviceTypeDebouncedQuery,
    serviceTypeQuery,
    truckCompanyDebouncedQuery,
    truckCompanyQuery
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
